import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../utils/api';
import './roles.css';
import ClearFilter from '../../components/ClearFilter';
import ExportTable from '../../components/ExportTable';
import Pagination from '../../components/Pagination';

const Roles = () => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [searchUsername, setSearchUsername] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('none');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    username: true,
    email: true,
    managerAction: true,
    adminAction: true,
    department: true,
    designation: true,
    reportingManager: true,
    region: true,
    lastAccessed: true,
  });

  const columnDropdownRef = useRef(null);

  const toggleColumn = (col) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [col]: !prev[col]
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const departmentsURL = `${window._env_.API_BASE_URL}/admincourse/departments/`;

  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      await Promise.all([fetchDepartments(), fetchUsers()]);
      setIsFetching(false);
    };
    fetchData();
  }, []);

  const fetchDepartments = async () => {
    try {
      const data = await apiFetch(departmentsURL);
      setDepartments(data.departments || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiFetch(`${window._env_.API_BASE_URL}/adminroles/users/`);
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleToggleAdmin = async (user) => {
    const newAdmin = !user.is_admin;
    setLoading((prev) => ({ ...prev, [user.user_id]: { ...prev[user.user_id], admin: true } }));

    try {
      await apiFetch(`${window._env_.API_BASE_URL}/adminroles/users/${user.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_admin: newAdmin })
      });
      await fetchUsers();
      setSnackbar({
        open: true,
        message: `Successfully ${user.email} has been ${newAdmin ? 'made admin' : 'removed as admin'}.`
      });
      setTimeout(() => setSnackbar({ open: false, message: '' }), 3000);
    } catch (err) {
      console.error('Error updating admin status:', err);
    } finally {
      setLoading((prev) => ({ ...prev, [user.user_id]: { ...prev[user.user_id], admin: false } }));
    }
  };

  const handleToggleManager = async (user) => {
    const newManager = !user.IsManager;
    setLoading((prev) => ({ ...prev, [user.user_id]: { ...prev[user.user_id], manager: true } }));

    try {
      await apiFetch(`${window._env_.API_BASE_URL}/adminroles/users/${user.user_id}/manager`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ IsManager: newManager })
      });
      await fetchUsers();
      setSnackbar({
        open: true,
        message: `Successfully ${user.email} has been ${newManager ? 'made manager' : 'removed as manager'}.`
      });
      setTimeout(() => setSnackbar({ open: false, message: '' }), 3000);
    } catch (err) {
      console.error('Error updating manager status:', err);
    } finally {
      setLoading((prev) => ({ ...prev, [user.user_id]: { ...prev[user.user_id], manager: false } }));
    }
  };

  const handleClearFilters = () => {
    setSearchUsername('');
    setSearchEmail('');
    setDeptFilter('');
    setSortOrder('none');
    setCurrentPage(1);
  };

  const getDeptName = (id) => departments.find((dept) => dept.id === id)?.name || 'Unknown';

  const parseLastLogin = (lastLogin) => {
    if (!lastLogin || lastLogin === 'Unknown') return null;
    const [date, time] = lastLogin.split(' ');
    const [day, month, year] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute);
  };

  const filteredUsers = users
    .filter((user) => {
      const matchesUsername = user.username.toLowerCase().includes(searchUsername.toLowerCase());
      const matchesEmail = user.email.toLowerCase().includes(searchEmail.toLowerCase());
      const matchesDept = deptFilter ? user.department_id === parseInt(deptFilter) : true;
      return matchesUsername && matchesEmail && matchesDept;
    })
    .sort((a, b) => {
      if (sortOrder === 'none') return 0;
      const dateA = parseLastLogin(a.last_login);
      const dateB = parseLastLogin(b.last_login);
      if (!dateA && !dateB) return 0;
      if (!dateA) return sortOrder === 'desc' ? 1 : -1;
      if (!dateB) return sortOrder === 'desc' ? -1 : 1;
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  // ─── Helper functions for button labels ────────────────────────────────
  const getAdminButtonLabel = (user) => {
    if (loading[user.user_id]?.admin) {
      return user.is_admin ? 'Removing...' : 'Making...';
    }
    return user.is_admin ? 'Remove Admin' : 'Make Admin';
  };

  const getManagerButtonLabel = (user) => {
    if (loading[user.user_id]?.manager) {
      return user.IsManager ? 'Removing...' : 'Making...';
    }
    return user.IsManager ? 'Remove Manager' : 'Make Manager';
  };

  return (
    <div>
      <h1>User Management</h1>

      {/* Filters */}
      <div className="filters">
        <input
          type="text"
          placeholder="Search by Username"
          value={searchUsername}
          onChange={(e) => setSearchUsername(e.target.value)}
        />
        <input
          type="text"
          placeholder="Search by Email"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
        />
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
          <option value="">All Departments</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>
        <select
          value={sortOrder}
          onChange={(e) => {
            setSortOrder(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="none">Sort by Last Accessed</option>
          <option value="desc">Last Access: Recent to Oldest</option>
          <option value="asc">Last Access: Oldest to Recent</option>
        </select>
        <ClearFilter onClear={handleClearFilters} />
        <ExportTable
          data={filteredUsers}
          headers={[
            visibleColumns.username && 'Username',
            visibleColumns.email && 'Email',
            visibleColumns.managerAction && 'Manager Action',
            visibleColumns.adminAction && 'Admin Action',
            visibleColumns.department && 'Department',
            visibleColumns.designation && 'Designation',
            visibleColumns.reportingManager && 'Reporting Manager',
            visibleColumns.region && 'Region',
            visibleColumns.lastAccessed && 'Last Accessed(IST)'
          ].filter(Boolean)}
          tableName="User_Management"
        />

        <div className="column-selector" ref={columnDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            style={{
              padding: '9px 12px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              backgroundColor: '#fff',
              fontSize: '11px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500',
              color: '#333',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = '#007bff'}
            onMouseOut={(e) => e.currentTarget.style.borderColor = '#ccc'}
          >
            Columns ⚙️
          </button>
          {showColumnDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '12px',
              zIndex: 100,
              minWidth: '180px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginTop: '4px'
            }}>
              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#666', borderBottom: '1px solid #eee', paddingBottom: '4px', marginBottom: '2px' }}>
                Toggle Columns
              </span>
              {[
                { key: 'username', label: 'Username' },
                { key: 'email', label: 'Email' },
                { key: 'managerAction', label: 'Manager Action' },
                { key: 'adminAction', label: 'Admin Action' },
                { key: 'department', label: 'Department' },
                { key: 'designation', label: 'Designation' },
                { key: 'reportingManager', label: 'Reporting Manager' },
                { key: 'region', label: 'Region' },
                { key: 'lastAccessed', label: 'Last Accessed' }
              ].map((col) => (
                <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer', color: '#333', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[col.key]}
                    onChange={() => toggleColumn(col.key)}
                    style={{ cursor: 'pointer' }}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="user-table-scroll">
        <table className="user-table">
          <thead>
            <tr>
              {visibleColumns.username && <th>Username</th>}
              {visibleColumns.email && <th>Email</th>}
              {visibleColumns.managerAction && <th>Manager Action</th>}
              {visibleColumns.adminAction && <th>Admin Action</th>}
              {visibleColumns.department && <th>Department</th>}
              {visibleColumns.designation && <th>Designation</th>}
              {visibleColumns.reportingManager && <th>Reporting Manager</th>}
              {visibleColumns.region && <th>Region</th>}
              {visibleColumns.lastAccessed && <th>Last Accessed(IST)</th>}
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <tr>
                <td colSpan={Object.values(visibleColumns).filter(Boolean).length} style={{ color: 'black', textAlign: 'center' }}>
                  Loading...
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers
                .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                .map((user) => (
                  <tr key={user.user_id}>
                    {visibleColumns.username && <td>{user.username}</td>}
                    {visibleColumns.email && <td>{user.email}</td>}
                    {visibleColumns.managerAction && (
                      <td>
                        <button
                          className="admin-button"
                          disabled={loading[user.user_id]?.manager}
                          onClick={() => handleToggleManager(user)}
                        >
                          {getManagerButtonLabel(user)}
                        </button>
                      </td>
                    )}
                    {visibleColumns.adminAction && (
                      <td>
                        <button
                          className="admin-button"
                          disabled={loading[user.user_id]?.admin}
                          onClick={() => handleToggleAdmin(user)}
                        >
                          {getAdminButtonLabel(user)}
                        </button>
                      </td>
                    )}
                    {visibleColumns.department && <td>{getDeptName(user.department_id)}</td>}
                    {visibleColumns.designation && <td>{user.designation || 'N/A'}</td>}
                    {visibleColumns.reportingManager && <td>{user.reporting_manager || 'N/A'}</td>}
                    {visibleColumns.region && <td>{user.region || 'N/A'}</td>}
                    {visibleColumns.lastAccessed && <td>{user.last_login || 'Unknown'}</td>}
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan={Object.values(visibleColumns).filter(Boolean).length} style={{ color: 'red', textAlign: 'center' }}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        items={filteredUsers}
        itemsPerPage={rowsPerPage}
        setItemsPerPage={setRowsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      {/* Snackbar */}
      {snackbar.open && <div className="snackbar">{snackbar.message}</div>}
    </div>
  );
};

export default Roles;